import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { ChartBridge, ImagingCategoryEnum, MessageContentAttachmentMediaTypeEnum, PatientImages } from '@ffd/api-client';
import {
  appActions,
  selectBridges,
  selectHasImagingSoftwareBySiteId,
  selectPatientImages,
  selectTeeth,
  selectTeethLoading,
  selectTeethState,
  selectUserSiteId,
} from '@libs/store';
import { ToothWithLayers } from '@libs/ui-lib';
import { DataState, Loadable, routes, sortByDate, toDateFormat } from '@libs/utils';
import { Store } from '@ngrx/store';
import { OnboardingModalService, OnboardingTab } from '@libs/onboarding';
import {
  categorizeImages,
  ImagingCategoryTranslations,
  ImagingGalleryItem,
  mapImagesToFullSeries,
  sortByImagingCategory,
} from '@libs/imaging';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotesScreenService, VisitGroup } from '@libs/notes';
import { distinctUntilChanged, map, skip } from 'rxjs';
import { ToothDetailsImages } from '../tooth-details-dialog/tooth-details-imaging';
import { chartingActions, selectLastToothPosition } from '../../store';

interface ChartingViewModel {
  teethWithLayers?: ToothWithLayers[];
  bridges: ChartBridge[];
  visits: VisitGroup[];
  visitsLoading: boolean;
  isTimeZoneDifferentFromLocal: boolean;
  toothDetailImages: Loadable<ToothDetailsImages[]>;
  loading: boolean;
  hasImagingSoftware: Loadable<boolean>;
}

@Injectable()
export class ChartingScreenComponentService {
  private readonly store = inject(Store);
  private readonly onboardingModalService = inject(OnboardingModalService);
  public activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly noteService = inject(NotesScreenService);

  private readonly siteId = computed(() => this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId);
  private readonly hasImagingSoftware = computed(() => this.store.selectSignal(selectHasImagingSoftwareBySiteId(this.siteId()))());

  public init(siteId?: string, patientId?: string): void {
    if (!siteId || !patientId) return;

    this.noteService.setPatientIdAndSiteId({ patientId, siteId });
    this.noteService.init();

    const lastToothPosition = this.store.selectSignal(selectLastToothPosition);

    if (lastToothPosition() !== undefined) {
      this.router.navigate([routes.toothDetails, lastToothPosition()], { relativeTo: this.activatedRoute });
    }

    const teethStateSignal = this.store.selectSignal(selectTeethState);

    if (![DataState.Loading, DataState.Fetched].includes(teethStateSignal())) {
      this.store.dispatch(chartingActions.paramsChanged({ siteId, patientId, examCount: 2, silentReload: false }));
    }

    this.store.dispatch(appActions.loadHasImagingSoftwareState({}));

    this.onboardingModalService.setActiveTab(OnboardingTab.Charting);

    this.activatedRoute.params
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((params) => {
          return { siteId: params['siteId'], patientId: params['patientId'] };
        }),
        skip(1),
        distinctUntilChanged((prev, curr) => prev.siteId === curr.siteId && prev.patientId === curr.patientId),
      )
      .subscribe((params) => {
        this.store.dispatch(
          chartingActions.paramsChanged({ siteId: params['siteId'], patientId: params['patientId'], examCount: 2, silentReload: true }),
        );
      });
  }

  public viewModel = computed<ChartingViewModel>(() => {
    const loading = this.store.selectSignal(selectTeethLoading)();
    const images = this.store.selectSignal(selectPatientImages)().data;
    const imagesState = this.store.selectSignal(selectPatientImages)().state;
    const hasImagingSoftware = this.hasImagingSoftware();

    const teethWithLayers = this.store.selectSignal(selectTeeth)()?.teethWithLayers;
    const bridges = this.store.selectSignal(selectBridges)();

    return {
      teethWithLayers,
      bridges,
      toothDetailImages: {
        data: this.groupPatientImagesByType(images),
        state: imagesState,
      },
      visits: this.noteService.viewModel().visits,
      visitsLoading: this.noteService.viewModel().visitsLoading,
      isTimeZoneDifferentFromLocal: !!this.noteService.viewModel().isSiteTimeZoneDifferentFromClient,
      loading: loading,
      hasImagingSoftware: hasImagingSoftware,
    };
  });

  public deleteProgressNote = (id: number): void => {
    this.noteService.deleteProgressNote(id);
  };

  public hideCondition = (noteId: number): void => {
    this.noteService.hideCondition(noteId);
  };

  //TODO [FFDWEB-589]: refactor this later on as well
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private readonly groupPatientImagesByType = (patientImages?: PatientImages): ToothDetailsImages[] => {
    if (!patientImages || !patientImages.images) {
      return [];
    }

    const uncategorizedImages: ImagingGalleryItem[] = patientImages.images.map((image) => ({
      ...image,
      category: image.type,
      groupImageId: image.id,
      groupLength: 1,
      date: image.date ? toDateFormat(new Date(image.date)) : image.date,
      mediaType: MessageContentAttachmentMediaTypeEnum.IMAGE,
    }));

    // We map the images to a destructured full series to have the same structure as the Digital page
    // and combine them with images that are not part of the full series. After this we short the images by category
    const combinedImages = [
      ...mapImagesToFullSeries(uncategorizedImages, true),
      ...uncategorizedImages.filter(
        (image) => image.category !== ImagingCategoryEnum.BITEWINGS && image.category !== ImagingCategoryEnum.PERIAPICALS,
      ),
    ].sort(sortByImagingCategory);
    const groupedFullSeriesImages = categorizeImages(combinedImages, false);

    return Object.entries(groupedFullSeriesImages).map(([type, images]) => ({
      group: ImagingCategoryTranslations[type] ?? type,
      images: images.sort((a, b) => sortByDate({ date: a.date }, { date: b.date }, true)),
    }));
  };
}
